package br.com.duoma.duomaproducao.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.duoma.duomaproducao.ui.misc.isCompactScreen
import sh.calvin.reorderable.ReorderableItem
import sh.calvin.reorderable.rememberReorderableLazyGridState
import sh.calvin.reorderable.rememberReorderableLazyListState


@Composable
fun <T> Filter(list: List<Pair<T, Boolean>>, onMove: ((Int, Int) -> Unit)?, onClick: (T) -> Unit) {
    val colorScheme = MaterialTheme.colorScheme
    val isCompact = isCompactScreen()

    // All remember calls are unconditional — Compose rules forbid conditional remember.
    val lazyListState = rememberLazyListState()
    val listReorderState = rememberReorderableLazyListState(lazyListState) { from, to ->
        onMove?.invoke(from.index, to.index)
    }
    val lazyGridState = rememberLazyGridState()
    val gridReorderState = rememberReorderableLazyGridState(lazyGridState) { from, to ->
        onMove?.invoke(from.index, to.index)
    }

    val containerModifier = Modifier
        .clip(RoundedCornerShape(5.dp))
        .background(colorScheme.primaryContainer)
        .padding(2.dp)

    // Measure the widest label so every grid cell is guaranteed to fit its text.
    val textMeasurer = rememberTextMeasurer()
    val labelStyle = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold)
    val density = LocalDensity.current
    val cellHorizontalPadding = 4.dp // each side, matches the Box padding below
    val minCellWidth: Dp = remember(list, labelStyle) {
        val maxTextPx = list.maxOfOrNull { (item, _) ->
            textMeasurer.measure(item.toString(), labelStyle).size.width
        } ?: 0
        with(density) { maxTextPx.toDp() } + cellHorizontalPadding * 2
    }

    if (isCompact) {
        // ── Mobile: LazyVerticalGrid ─────────────────────────────────────────
        // Wraps items into rows (like FlowRow) while keeping reorder support.
        // Long-press to drag avoids conflicts with tap and scroll gestures.
        BoxWithConstraints(containerModifier.fillMaxWidth()) {
            // colCount mirrors what GridCells.Adaptive(minCellWidth) will produce.
            val spacing = 2f
            val colCount = maxOf(1, ((maxWidth.value + spacing) / (minCellWidth.value + spacing)).toInt())
            val rowCount = (list.size + colCount - 1) / colCount
            // Pre-compute exact height so LazyVerticalGrid gets a bounded constraint.
            val gridHeight = 28.dp * rowCount.coerceAtLeast(1) + 2.dp * (rowCount - 1).coerceAtLeast(0)

            LazyVerticalGrid(
                columns = GridCells.Adaptive(minCellWidth),
                state = lazyGridState,
                modifier = Modifier.fillMaxWidth().height(gridHeight),
                verticalArrangement = Arrangement.spacedBy(2.dp),
                horizontalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                items(list, key = { it.first as Any }) { item ->
                    ReorderableItem(gridReorderState, key = item.first as Any) { _ ->
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(28.dp)
                                .clip(RoundedCornerShape(3.dp))
                                .background(
                                    if (item.second) colorScheme.surface
                                    else colorScheme.primaryContainer
                                )
                                .draggableHandle(enabled = onMove != null)
                                .clickable { onClick(item.first) }
                                .padding(horizontal =  4.dp),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                text = item.first.toString(),
                                style = MaterialTheme.typography.labelSmall.copy(
                                    fontWeight = FontWeight.Bold,
                                    color = if (item.second) colorScheme.primary
                                            else colorScheme.onSurfaceVariant,
                                ),
                                textAlign = TextAlign.Center,
                                maxLines = 1,
                            )
                        }
                    }
                }
            }
        }
    } else {
        // ── Desktop: LazyRow with connected strip + drag-to-reorder ─────────
        LazyRow(
            modifier = containerModifier,
            state = lazyListState,
            horizontalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            items(list, { it.first as Any }) { item ->
                val shape = when {
                    item == list.first() -> RoundedCornerShape(topStart = 4.dp, bottomStart = 4.dp)
                    item == list.last()  -> RoundedCornerShape(topEnd = 4.dp, bottomEnd = 4.dp)
                    else                 -> RectangleShape
                }
                ReorderableItem(listReorderState, key = item.first as Any) {
                    Box(
                        Modifier
                            .background(
                                if (item.second) colorScheme.surface else colorScheme.primaryContainer,
                                shape
                            )
                            .draggableHandle(onMove != null)
                            .clickable { onClick(item.first) }
                            .padding(horizontal = 8.dp, vertical = 6.dp)
                    ) {
                        Text(
                            text = item.first.toString(),
                            style = MaterialTheme.typography.bodySmall.copy(
                                fontWeight = FontWeight.Bold,
                                color = if (item.second) colorScheme.primary
                                        else colorScheme.onSurfaceVariant,
                            )
                        )
                    }
                }
            }
        }
    }
}